import {
  ArrowLeft,
  BellRing,
  Copy,
  PauseCircle,
  Share2,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { TimelinePayload } from "@vzlive/shared";
import { t } from "@vzlive/shared/i18n";
import { BrandMark } from "../components/BrandMark";
import { ChatDock } from "../components/ChatDock";
import { Countdown } from "../components/Countdown";
import { EmbedReader } from "../components/EmbedReader";
import { LiveStats } from "../components/LiveStats";
import { SetlistStrip } from "../components/Setlist";
import { StatusBadge } from "../components/StatusBadge";
import { StickyAudio } from "../components/StickyAudio";
import { SubmissionForm } from "../components/SubmissionForm";
import { LiveTimeline } from "../components/LiveTimeline";
import { tenantTheme } from "../lib/branding";
import { useTimeline } from "../hooks/useTimeline";

export function ReaderPage({
  embed = false,
  slugOverride,
}: {
  embed?: boolean;
  slugOverride?: string;
}) {
  const params = useParams();
  const slug = slugOverride ?? params.slug ?? "";
  const { timeline, stats, state, reload, onChatEvent } = useTimeline(slug);
  const [copied, setCopied] = useState(false);
  const composerRef = useRef<HTMLDialogElement>(null);
  const lastTrigger = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!timeline) return;
    document.documentElement.lang = timeline.tenant.locale;
    document.title = `${timeline.event.title} · ${timeline.tenant.name}`;
    if (!embed) return;
    // The host supplies its own origin via ?parent= so height messages can
    // be sent as targeted messages instead of a wildcard broadcast.
    const parent = new URLSearchParams(window.location.search).get("parent");
    const targetOrigin =
      parent && /^https?:\/\//.test(parent) ? parent.replace(/\/$/, "") : "*";
    let lastHeight = -1;
    const postHeight = () => {
      const height = document.documentElement.scrollHeight;
      if (height === lastHeight) return;
      lastHeight = height;
      window.parent.postMessage(
        { type: "vzlive:height", height },
        targetOrigin,
      );
    };
    const observer = new ResizeObserver(postHeight);
    observer.observe(document.body);
    postHeight();
    return () => observer.disconnect();
  }, [
    embed,
    timeline?.event.id,
    timeline?.event.title,
    timeline?.tenant.name,
    timeline?.tenant.locale,
  ]);

  // Nach dem Schließen des Composer-Dialogs landet der Fokus auf dem Button.
  useEffect(() => {
    const dialog = composerRef.current;
    if (!dialog) return;
    const onClose = () => lastTrigger.current?.focus();
    dialog.addEventListener("close", onClose);
    return () => dialog.removeEventListener("close", onClose);
  }, []);

  if (!timeline && state === "loading") {
    return (
      <div className="page-loader">
        <BrandMark />
        <span />
      </div>
    );
  }
  if (!timeline) {
    return (
      <main className="reader-error" id="main">
        <BrandMark />
        <h1>
          {state === "offline"
            ? "Gerade keine Verbindung."
            : "Event nicht gefunden."}
        </h1>
        <p>
          {state === "offline"
            ? "Sobald du wieder online bist, aktualisiert sich der Ticker."
            : "Diese Live-Seite existiert nicht oder ist nicht mehr verfügbar."}
        </p>
        <button onClick={() => void reload()}>Erneut versuchen</button>
      </main>
    );
  }

  const { tenant, event } = timeline;
  const locale = tenant.locale;
  const setlistPlugin = timeline.plugins.find(
    (item) => item.id === "setlist.u2tour",
  );

  if (embed) {
    return (
      <EmbedReader
        timeline={timeline}
        stats={stats}
        connectionState={state}
        onChatEvent={onChatEvent}
      />
    );
  }

  const share = async () => {
    const url = `${window.location.origin}/api/public/events/${event.slug}/share`;
    if (navigator.share) {
      await navigator.share({
        title: event.title,
        text: event.description,
        url,
      });
      return;
    }
    await navigator.clipboard.writeText(url);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1_500);
  };

  return (
    <div
      className="reader-page"
      style={tenantTheme(tenant.primaryColor, tenant.secondaryColor)}
    >
      <header className="reader-nav">
        <Link
          to="/"
          aria-label={t(locale, "allEvents")}
          className="reader-nav__back"
        >
          <ArrowLeft size={17} />
          {tenant.logoUrl ? (
            <img src={tenant.logoUrl} alt={tenant.name} />
          ) : (
            <strong>{tenant.name}</strong>
          )}
        </Link>
        <button onClick={() => void share()} className="reader-nav__share">
          {copied ? <Copy size={17} /> : <Share2 size={17} />}
          {copied ? t(locale, "copied") : t(locale, "share")}
        </button>
      </header>

      {state === "offline" && (
        <div className="offline-notice" role="status">
          Offline · letzter gespeicherter Stand
        </div>
      )}

      <main id="main">
        <section className="event-hero">
          <div className="event-hero__meta">
            <StatusBadge status={event.status} locale={locale} />
            <time dateTime={event.startsAt}>
              {new Intl.DateTimeFormat(locale, {
                weekday: "long",
                day: "2-digit",
                month: "long",
                hour: "2-digit",
                minute: "2-digit",
                timeZone: event.timezone,
              }).format(new Date(event.startsAt))}
            </time>
          </div>
          <h1>{event.title}</h1>
          <div className="event-hero__details">
            <p>{event.description}</p>
            {event.venue && <strong>{event.venue}</strong>}
          </div>
          {tenant.submissionsEnabled && event.status !== "archived" && (
            <div className="event-hero__actions">
              <button
                type="button"
                className="moment-button"
                onClick={(e) => {
                  lastTrigger.current = e.currentTarget;
                  composerRef.current?.showModal();
                }}
              >
                <span aria-hidden="true">＋</span>{" "}
                {locale === "en" ? "Send message" : "Nachricht senden"}
              </button>
            </div>
          )}
          {event.status === "scheduled" &&
            new Date(event.startsAt) > new Date() && (
              <Countdown target={event.startsAt} locale={locale} />
            )}
        </section>

        {(tenant.isPaused || event.status === "paused") && (
          <section className="pause-panel">
            <PauseCircle />
            <div>
              <h2>Wir sind gleich wieder da.</h2>
              <p>Der bisherige Live-Ticker bleibt vollständig lesbar.</p>
            </div>
          </section>
        )}

        {timeline.alert && (
          <aside
            className={`alert-banner alert-banner--${timeline.alert.tone}`}
          >
            <BellRing size={18} />
            <strong>{timeline.alert.text}</strong>
          </aside>
        )}

        <section className="timeline-section">
          {setlistPlugin && (
            <SetlistStrip payload={setlistPlugin.payload} locale={locale} />
          )}
          <header className="timeline-heading">
            <div>
              <h2>{t(locale, "timeline")}</h2>
            </div>
            {stats && <LiveStats stats={stats} locale={locale} />}
          </header>
          <LiveTimeline key={event.id} timeline={timeline} />
        </section>
      </main>

      <footer className="reader-footer">
        <BrandMark />
        <span>{t(locale, "productBy")} vonzwei.media</span>
        <nav>
          {tenant.legal.imprintUrl && (
            <a href={tenant.legal.imprintUrl}>Impressum</a>
          )}
          {tenant.legal.privacyUrl && (
            <a href={tenant.legal.privacyUrl}>Datenschutz</a>
          )}
        </nav>
      </footer>

      <dialog
        ref={composerRef}
        className="pulse-dialog"
        aria-labelledby="moment-compose-title"
      >
        <div className="pulse-dialog__head">
          <h2 id="moment-compose-title">
            {locale === "en"
              ? "Send status update to the editors"
              : "Status Update an die Redaktion senden"}
          </h2>
          <button
            type="button"
            className="pulse-dialog__close"
            aria-label={locale === "en" ? "Close form" : "Formular schließen"}
            onClick={() => composerRef.current?.close()}
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>
        <div className="pulse-dialog__content">
          <SubmissionForm eventId={event.id} locale={locale} compact />
        </div>
      </dialog>

      {tenant.chatEnabled &&
        event.chatEnabled &&
        event.status !== "archived" && (
          <ChatDock
            tenant={tenant}
            event={event}
            locale={locale}
            onChatEvent={onChatEvent}
            onlineNow={stats?.onlineNow}
          />
        )}
      {timeline.stickyMedia && (
        <StickyAudio media={timeline.stickyMedia} locale={locale} />
      )}
    </div>
  );
}
